/**
 * Storefront-side integration events.
 *
 * Connected integrations publish a few NON-secret ids through
 * `site_settings.integrations_public` (served by /api/settings/public). This
 * module loads each vendor's script once and fans storefront actions out to
 * whichever adapters are configured. Every call is a guarded no-op when the
 * vendor is absent, so pages can call these unconditionally.
 */

import type { StorefrontAdapter, IntegrationsPublicConfig, StorefrontProduct, StorefrontCartItem, StorefrontOrder } from './types';
import { klaviyoAdapter } from './adapters/klaviyo';
import { mailchimpAdapter } from './adapters/mailchimp';
import { yotpoAdapter } from './adapters/yotpo';
import { googleTagsAdapter } from './adapters/google-tags';
import { metaAdapter } from './adapters/meta';
import { omnisendAdapter } from './adapters/omnisend';
import { stampedAdapter } from './adapters/stamped';
import { setTrackingConsent } from './consent';

export type { IntegrationsPublicConfig, StorefrontProduct, StorefrontCartItem, StorefrontOrder } from './types';

const ADAPTERS: StorefrontAdapter[] = [klaviyoAdapter, mailchimpAdapter, yotpoAdapter, googleTagsAdapter, metaAdapter, omnisendAdapter, stampedAdapter];

let current: IntegrationsPublicConfig = {};
let initializedFor = '';

function active(): Array<{ adapter: StorefrontAdapter; cfg: Record<string, string> }> {
  const out: Array<{ adapter: StorefrontAdapter; cfg: Record<string, string> }> = [];
  for (const adapter of ADAPTERS) {
    const cfg = current[adapter.id];
    if (cfg && adapter.isConfigured(cfg)) out.push({ adapter, cfg });
  }
  return out;
}

function safe(fn: () => void) {
  try {
    fn();
  } catch (err) {
    if (import.meta.env.DEV) console.warn('[integrations] storefront adapter threw', err);
  }
}

/** Idempotent: re-running with the same config does nothing. */
export function initIntegrations(config: IntegrationsPublicConfig | null | undefined): void {
  if (typeof window === 'undefined') return;
  current = config && typeof config === 'object' ? config : {};
  const key = JSON.stringify(current);
  if (key === initializedFor) return;
  initializedFor = key;
  for (const { adapter, cfg } of active()) safe(() => adapter.load(cfg));
}

export function identify(user: { email?: string | null; first_name?: string | null; last_name?: string | null; id?: string | null } | null): void {
  if (!user?.email) return;
  for (const { adapter } of active()) safe(() => adapter.identify?.(user));
}

export function trackViewedProduct(product: StorefrontProduct): void {
  for (const { adapter } of active()) safe(() => adapter.viewedProduct?.(product));
}

export function trackAddedToCart(item: StorefrontCartItem, cart: StorefrontCartItem[]): void {
  for (const { adapter } of active()) safe(() => adapter.addedToCart?.(item, cart));
}

export function trackStartedCheckout(cart: StorefrontCartItem[], total: number): void {
  for (const { adapter } of active()) safe(() => adapter.startedCheckout?.(cart, total));
}

export function trackOrderCompleted(order: StorefrontOrder): void {
  for (const { adapter } of active()) safe(() => adapter.orderCompleted?.(order));
}

/**
 * Consent banner hook: grant (or revoke) tracking consent and re-run the
 * adapter loads so tags that waited for consent start now.
 */
export function grantTrackingConsent(granted = true): void {
  setTrackingConsent(granted);
  if (!granted) return;
  initializedFor = '';
  initIntegrations(current);
}

export function refreshWidgets(): void {
  for (const { adapter } of active()) safe(() => adapter.refresh?.());
}

/** Test seam. */
export function _resetIntegrationsForTests(): void {
  current = {};
  initializedFor = '';
}

/** Test seam: swap the adapter list. */
export function _setAdaptersForTests(adapters: StorefrontAdapter[] | null): void {
  ADAPTERS.splice(0, ADAPTERS.length, ...(adapters ?? [klaviyoAdapter, mailchimpAdapter, yotpoAdapter, googleTagsAdapter, metaAdapter, omnisendAdapter, stampedAdapter]));
}
